import { Editor } from '@tiptap/core'
import { useEffect, useState } from 'react'
import { DocumentState, ReferencesInfo } from '../../redux/types'
import { useDispatch, useSelector } from 'react-redux'
import { selectUser } from '../../redux/systemSlice'
import { useCookieUser } from '../../hooks/cookieUser'
import { LuArrowUpRight, LuLoaderCircle, LuPlus } from 'react-icons/lu'
import { getAuthorReference } from '../../helpers/strings'
import { selectDocumentState, setReferences, setUserWriterSelections } from '../../redux/docSlice'
import { useDocId } from '../../hooks/docID'
import { useAuth } from '../../contexts/Auth'
import { usePostHog } from 'posthog-js/react'

const SelectReferenceMenu = ({
	editor,
	currentSelection,
	setMenuOpen,
}: {
	editor: Editor
	currentSelection: string
	setMenuOpen: (open: boolean) => void
}) => {
	const posthog = usePostHog()
	const docID = useDocId()
	const [referenceResults, setReferenceResults] = useState<ReferencesInfo[]>([])
	const [referencesLoading, setReferencesLoading] = useState(false)
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docID))
	const user = useSelector(selectUser)
	const { userIDCookie } = useCookieUser()
	const dispatch = useDispatch()
	const { currentUser } = useAuth() as any

	const invalidAuthorList = (authors: string[]) => {
		if (authors.length === 0) return true
		return authors[0].includes('Author') || authors[0].includes('Name')
	}

	useEffect(() => {
		if (!currentSelection || (!user.id && !userIDCookie)) return
		setReferencesLoading(true)
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				sentence: currentSelection,
				userId: user.id ?? userIDCookie,
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/generate-sentence-references/', requestOptions)
			.then((res) => res.json())
			.then((response: { allReferences: ReferencesInfo[] }) => {
				if (response.allReferences) {
					setReferenceResults(response.allReferences)
				}
				setReferencesLoading(false)
			})
	}, [currentSelection, user.id, userIDCookie])

	const addCitation = async (ref: ReferencesInfo) => {
		posthog.capture('add-citation')
		// Only add the reference if it's not already in the document
		if (!documentState?.references?.some((existingRef) => existingRef.url === ref.url)) {
			let newReferences = [...(documentState?.references ?? [])]
			newReferences.push(ref)

			if (!documentState.userWriterSelections.refStyle) {
				dispatch(
					setUserWriterSelections({
						docID: docID,
						userWriterSelections: { ...documentState.userWriterSelections, refStyle: 'APA_7' },
					})
				)
			}

			dispatch(
				setReferences({
					docID: docID,
					references: newReferences,
				})
			)
			const requestOptions = {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
				},
				body: JSON.stringify({
					id: docID,
					references: newReferences,
				}),
			}
			fetch(process['env']['REACT_APP_API_ROOT'] + '/document/update/', requestOptions)
		}

		// Get the current selection
		const { from, to } = editor.state.selection

		// Get the text at the selection
		const selectedText = editor.state.doc.textBetween(from, to)

		// Format the citation
		const year = ref.publishedDate ? new Date(ref.publishedDate).getFullYear() : ''
		let citationText = ''

		if (ref.authors && ref.authors.length > 0 && !invalidAuthorList(ref.authors)) {
			const authorLastName = getAuthorReference(ref.authors, 'APA_7')
			citationText = `(${authorLastName}, ${year})`
		} else if (ref.title) {
			// For APA 7, use first few words of title (up to 3 words) when no valid authors
			const titleWords = ref.title.split(' ')
			const shortenedTitle = titleWords.slice(0, 3).join(' ')
			citationText = `("${shortenedTitle}", ${year})`
		} else {
			// Fallback if neither authors nor title available
			citationText = `(n.d.)`
		}

		// Check if there's a period at the end of the selection
		const hasPeriod = selectedText.trim().endsWith('.')

		// Insert the citation
		editor
			.chain()
			.focus()
			.insertContentAt(hasPeriod ? to - 1 : to, ` <span data-hover-menu="true">${citationText}</span>`)
			.run()
		setMenuOpen(false)
	}
	if (!editor) return null

	return referencesLoading || referenceResults.length === 0 ? (
		<div className="flex justify-center items-center mt-16">
			{LuLoaderCircle({ className: 'animate-spin h-8 w-8' })}
		</div>
	) : (
		<div className="flex flex-col gap-2">
			<div className={`flex flex-col max-h-96 overflow-y-auto relative mt-2`}>
				{referenceResults.map((ref, index) => (
					<div
						key={index}
						className="px-2 py-3 border-b border-gray-300 flex flex-col gap-0.5 cursor-pointer hover:bg-gray-50"
						onClick={() => addCitation(ref)}
					>
						<div className="text-gray-600 text-xs">Research Paper</div>
						<div className="text-xs font-medium truncate">{ref.title}</div>
						{(ref.authors?.length ?? 0) > 0 && <div className="text-gray-600 text-xs">{ref.authors?.[0]}</div>}
						<div className="text-gray-600 text-xs">
							{ref.publishedDate ? new Date(ref.publishedDate).getFullYear() : ''}
						</div>
						<div className="text-gray-600 text-xs line-clamp-3 border-l border-gray-300 pl-2">{ref.summary}</div>
						<div className="flex gap-4 mt-1">
							<div className="text-link text-xs font-semibold hover:underline">
								{LuPlus({ className: 'inline-block mr-1' })}Cite
							</div>
							<div
								className="text-xs font-semibold hover:underline"
								onClick={(e) => {
									e.stopPropagation()
									window.open(ref.url, '_blank')
								}}
							>
								{LuArrowUpRight({ className: 'inline-block mr-1' })}View
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

export default SelectReferenceMenu
